"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import { TextEffect } from "@/components/ui/text-effect";
import { SpringElement } from "@/components/ui/spring-element";

interface FirstScreenProps {
  deform: ReturnType<typeof useMotionValue>;
  intensity: number;
}

export function FirstScreen({ deform, intensity }: FirstScreenProps) {
  const firstScreenRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: firstScreenRef,
    offset: ["start start", "end end"],
  });

  const firstScreenScale = useTransform(scrollYProgress, [0, 1], [1, 0.2]);
  const firstScreenY = useTransform(scrollYProgress, [0, 1], [0, 1000]);

  const rotateX = useTransform(deform, (v: number) => v * -5);
  const skewY = useTransform(deform, (v: number) => v * -1.5);
  const scaleY = useTransform(deform, (v: number) => 1 + v * intensity);
  const scaleX = useTransform(deform, (v: number) => 1 - v * intensity * 0.6);

  return (
    <div ref={firstScreenRef} className="h-[calc(100dvh-80px)] flex flex-col">
      <motion.div
        layoutId="container"
        className="flex-1 p-4 bg-transparent overflow-hidden relative"
        style={{
          borderRadius: "24px",
          rotateX,
          skewY,
          scaleY,
          scaleX,
          originX: 0.5,
          originY: 0,
          transformPerspective: 500,
          willChange: "transform",
          scale: firstScreenScale,
          y: firstScreenY,
        }}
      >
        <div
          className="flex flex-col items-start justify-center h-full px-12 relative"
          data-cursor-zone="white-content"
        >
          <motion.h1
            className="text-8xl font-bold text-left text-white "
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1.0,
            }}
          >
            Fellou
          </motion.h1>
          <h2 className="sr-only">The World's First Agentic Browser</h2>
          <h2 className="text-9xl font-bold text-left text-white ">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: 0.5,
              }}
            >
              The W
              <SpringElement
                className="text-7xl relative -top-[7px] inline-block"
                springClassName="stroke-white dark:stroke-white stroke-[4px]"
                dragElastic={0.3}
                springConfig={{ stiffness: 150, damping: 20 }}
                springPathConfig={{
                  coilCount: 6,
                  amplitudeMin: 10,
                  amplitudeMax: 25,
                  curveRatioMin: 0.6,
                  curveRatioMax: 1.2,
                  bezierOffset: 12,
                }}
              >
                <span aria-label="O" className="block select-none">
                  🌏
                </span>
              </SpringElement>
              rld's{" "}
            </motion.span>
            <motion.span
              initial={{ x: -200, opacity: 0, scale: 0.8, filter: "blur(4px)", skewX: 10 }}
              animate={{ x: 0, opacity: 1, scale: 1, filter: "blur(0px)", skewX: -10 }}
              transition={{
                delay: 0.8,
              }}
              className="inline-block"
            >
              First
            </motion.span>
            <br />
            <div className="flex gap-4 font-serif ">
              <motion.div layoutId="agentic">Agentic</motion.div>
              <motion.div layoutId="browser">Browser</motion.div>
            </div>
          </h2>
        </div>
        <TextEffect
          per="line"
          as="p"
          delay={1.2}
          className="absolute bottom-6 right-6 text-right text-white text-base font-bold"
          segmentWrapperClassName="overflow-hidden block"
          variants={{
            container: {
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.2 },
              },
            },
            item: {
              hidden: {
                opacity: 0,
                y: 40,
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.4,
                },
              },
            },
          }}
        >
          {`Go beyond browsing with an AI Browser that takes automated action
Giving you freedom to pursue your Renaissance self
`}
        </TextEffect>
      </motion.div>
    </div>
  );
}